<template>
    <div ref="map" class="relative -mx-6 md:mx-0 mb-36 md:mb-28 bg-primary-background">
        <!-- Google Map -->
        <gmaps-map
            class="h-96 md:h-[800px] w-full asset-map"
            :markers="markers"
            @on-map-mount="(onMapMount)"
            @on-map-error="(onMapError)"
        />

        <!-- Product Card holder - absolute position -->
        <transition-group name="fade">
            <dialog v-if="activeAsset && isFullscreen" class="fixed mt-0 top-[unset] bottom-3 md:top-0 md:m-8 2xl:ml-[5rem] 2xl:left-[15%] w-full h-32 md:w-[424px] md:h-[634px]">
                <transition-group name="fade-delay">
                    <product-card
                        v-for="marker in filteredMarkers"
                        :key="marker.assetData.id"
                        :data-content="marker.assetData"
                        class="absolute top-0 left-0 hidden md:block"
                    />
                </transition-group>
                <transition-group name="fade-delay">
                    <div v-for="marker in filteredMarkers" :key="marker.assetData.id" class="absolute top-0 left-0 h-32 md:h-auto flex items-start md:hidden border-b border-b-grey-pastel fullscreen-card">
                        <div class="flex w-28 shrink-0 place-self-stretch">
                            <resized-image :data="activeAsset.mapImage" classes="place-self-stretch w-28 object-cover" />
                        </div>
                        <div class="grow px-6 py-2">
                            <div class="text-brand-xs font-medium mb-1 text-grey-muted">
                                {{ activeAsset.type }}
                            </div>
                            <div class="mb-1 text-brand-md font-medium text-primary-text line-clamp-1 md:line-clamp-3">
                                {{ activeAsset.name }}
                            </div>
                            <div class="line-clamp-2 md:line-clamp-3 text-brand-sm">
                                {{ activeAsset.description }}
                            </div>
                        </div>
                        <stockland-button
                            additional-button-classes="shrink-0 place-self-center mr-6 border-black"
                            :is-visible="true"
                            :is-loading="false"
                            type="link"
                            layout="icon-only"
                            variant="style-4"
                            size="medium"
                            :icon="activeAsset.button.icon ?? 'arrow-right'"
                            :target="activeAsset.button.target"
                            :href="activeAsset.button.link"
                        />
                    </div>
                </transition-group>
                <stockland-button
                    additional-icon-classes="!fill-white"
                    additional-button-classes="absolute right-0 top-0 hidden md:block p-3"
                    :is-visible="true"
                    :is-loading="false"
                    type="button"
                    layout="icon-only"
                    variant="style-6"
                    size="medium"
                    icon="xmark"
                    @button-clicked="(closeProductCard)"
                />
            </dialog>
            <div v-if="activeAsset && !isFullscreen" class="absolute bottom-0 md:bottom-none md:top-0 left-0 translate-y-full md:translate-y-0 w-full h-28 md:w-[424px] md:h-[634px] md:m-8">
                <transition-group name="fade-delay">
                    <product-card
                        v-for="marker in filteredMarkers"
                        :key="marker.assetData.id"
                        :data-content="marker.assetData"
                        class="absolute top-0 left-0 hidden md:block"
                    />
                </transition-group>
                <transition-group name="fade-delay">
                    <div v-for="marker in filteredMarkers" :key="marker.assetData.id" class="absolute top-0 left-0 flex items-start md:hidden border-b border-b-grey-pastel">
                        <div class="flex w-28 shrink-0 place-self-stretch">
                            <resized-image :data="activeAsset.mapImage" classes="place-self-stretch w-28 object-cover" />
                        </div>
                        <div class="grow px-6 py-2">
                            <div class="text-brand-xs font-medium mb-1 text-grey-muted">
                                {{ activeAsset.type }}
                            </div>
                            <div class="mb-1 text-brand-md font-medium text-primary-text">
                                {{ activeAsset.name }}
                            </div>
                            <div class="line-clamp-3 text-brand-sm">
                                {{ activeAsset.description }}
                            </div>
                        </div>
                        <stockland-button
                            additional-button-classes="shrink-0 place-self-center mr-6 border-black"
                            :is-visible="true"
                            :is-loading="false"
                            type="link"
                            layout="icon-only"
                            variant="style-4"
                            size="medium"
                            :icon="activeAsset.button.icon ?? 'arrow-right'"
                            :target="activeAsset.button.target"
                            :href="activeAsset.button.link"
                        />
                    </div>
                </transition-group>
                <stockland-button
                    additional-icon-classes="!fill-white"
                    additional-button-classes="absolute right-0 top-0 hidden md:block p-3"
                    :is-visible="true"
                    :is-loading="false"
                    type="button"
                    layout="icon-only"
                    variant="style-6"
                    size="medium"
                    icon="xmark"
                    @button-clicked="(closeProductCard)"
                />
            </div>
        </transition-group>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, toRefs } from 'vue';
import { Cluster, defaultOnClusterClickHandler } from '@googlemaps/markerclusterer';

import { scrollToCoord } from '../../../../framework/scripts/utils/scroll';
import { GmapsMapController } from "../gmaps-map/gmaps-map-controller";

import { Asset, AssetMapProperties, AssetMapState } from './interfaces';


const useLocationMap = (props: AssetMapProperties) => {
    const state: AssetMapState = reactive({
        activeAsset: undefined,
        mapController: undefined,
        markers: props.assets,
        map: null,
        filteredMarkers: computed(() => state.markers.filter((m: Asset) => state.activeAsset && m.assetData.id === state.activeAsset.id)),
        isFullscreen: false
    });


    function onMapMount(mC: GmapsMapController) {
        state.mapController = mC;
        // This should be moved into a handler like below
        google.maps.event.addListener(state.mapController.map, "click", () => {
            state.activeAsset = undefined;
        });

        function isInFullScreen() {
            return (
                document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement
            ) != null;
        }

        function handleFullscreenChange() {
            state.isFullscreen = isInFullScreen();
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);


        state.mapController.setMarkerClickHandler((marker, markerData) => {
            // Set center on where the marker is
            const mc = state.mapController as GmapsMapController;
            state.activeAsset = markerData.assetData;
            mc.panTo(markerData.location);
            google.maps.event.addListenerOnce(mc.map, "idle", () => {
                mc.smoothZoom(12, mc.map.getZoom() ?? 12);
            });

            // This will scroll to make sure whole map is in view for tablet
            if(state.map !== null && window.innerWidth < 769 && !state.isFullscreen) {
                scrollToCoord(window.scrollY + state.map.getBoundingClientRect().bottom - state.map.getBoundingClientRect().height, 0)
            }

            // If we want to change the active icon we will need something like this + a reset for the icons
            // See what was done for LLC
            // mc.resetMarkerIcons();
            // mc.setActiveIcon(marker, 'stockland');

            setTimeout(() => {
                let dialog = document.querySelector('dialog')
                dialog?.showModal()
                dialog?.addEventListener('click', (event) => {
                    if (event.target === dialog) {
                        state.activeAsset = undefined;
                    }
                })
            }, 250)

        })
        state.mapController.setClusterClickHandler((event: google.maps.MapMouseEvent, cluster: Cluster, map: google.maps.Map) => {
            defaultOnClusterClickHandler(event, cluster, map);
            state.activeAsset = undefined;
        });

    }

    function onMapError(error: string) {
        console.error('Map error raised:', error);
    }

    function closeProductCard() {
        state.activeAsset = undefined;
        document.querySelector('dialog')?.close()
    }

    return {
        state,
        onMapError,
        onMapMount,
        closeProductCard,
    };
}

export default defineComponent({
    name: 'asset-map',
    props: {
        assets: {
            type: Array as PropType<Asset[]>,
            required: true,
        },
        header: {
            type: String,
            default: ''
        },
        headerAccent: {
            type: String,
            default: ''
        },
    },
    setup(props: AssetMapProperties) {
        const { state, ...methods } = useLocationMap(props);
        return {
            ...toRefs(state),
            ...methods
        };

    },
});
</script>
<style lang="postcss"></style>
